import React, { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { OneEmptyState } from '@factorialco/f0-react/dist/experimental'
import { Stack } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { Features } from 'generated/featureDefinitions'
import { useFeatures } from 'modules/core/hooks/useFeatures'

import { TicketCreateDialog } from './TicketCreateDialog'
import { TicketList } from 'modules/ticketing/components/TicketList/TicketList'
import { TicketDetailPanel } from 'modules/ticketing/components/TicketDetailPanel/TicketDetailPanel'
import { type TicketNode } from 'modules/ticketing/components/TicketList/ticketListConfig'

export const TicketListPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const openTicketId = useMemo(() => {
    const id = searchParams.get('id')
    return id || null
  }, [searchParams])

  const { has_dev_ai_ticketing_core, loadingFeatures } = useFeatures([
    Features.dev_ai_ticketing_core,
  ])

  const [openCreateDialog, setOpenCreateDialog] = useState(false)

  const closeDetailPanel = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('id')
      return next
    })
  }, [setSearchParams])

  const openTicketDetail = useCallback(
    (item: TicketNode) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.set('id', String(item.id))
        return next
      })
    },
    [setSearchParams],
  )

  const openCreatedTicket = useCallback(
    (ticketId: string) => {
      setOpenCreateDialog(false)
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.set('id', String(ticketId))
        return next
      })
    },
    [setSearchParams],
  )

  return (
    <>
      <ResponsivePage.Body
        variant="fullscreen"
        height="full"
        loading={loadingFeatures}
        authorized={has_dev_ai_ticketing_core}
        error={undefined}
      >
        {!loadingFeatures && !has_dev_ai_ticketing_core ? (
          <Stack gap="4" paddingY="p-4">
            <OneEmptyState
              title={i18n.t('ticketing.inbox.no_access_title')}
              description={i18n.t('ticketing.inbox.no_access_description')}
            />
          </Stack>
        ) : (
          <Stack gap="4" paddingY="p-4">
            <TicketList
              onOpenCreateDialog={() => setOpenCreateDialog(true)}
              onItemClick={openTicketDetail}
            />
          </Stack>
        )}
      </ResponsivePage.Body>
      {openCreateDialog && (
        <TicketCreateDialog
          onSuccess={openCreatedTicket}
          onClose={() => setOpenCreateDialog(false)}
        />
      )}
      {openTicketId != null && (
        <TicketDetailPanel ticketId={openTicketId} onClose={closeDetailPanel} />
      )}
    </>
  )
}

export default TicketListPage
